/* Yüklemenin kendisi — imzayı al, baytları R2'ye koy (plan §5.2 adım 6, §7).

   Türev üretimi bitti (derive.ts, pipeline.ts) ve imza ucu duruyor
   (`POST /api/admin/uploads/sign`). Eksik olan halka buydu: imzayı istemek
   ve dosyayı doğrudan kovaya yazmak.

   **Baytlar Worker'dan geçmiyor.** İmzalı PUT adresi tarayıcıya veriliyor ve
   tarayıcı R2'ye doğrudan yazıyor. Sebebi iki katlı: Worker'ın istek gövdesi
   sınırı 100 MB ve 2 GB'lık bir yığını Worker üzerinden akıtmak boşa trafik.
   Worker yalnızca yetkiyi veriyor, veriyi taşımıyor.

   **Anahtarı istemci seçmiyor.** Gövdede yalnızca kare kimliği ve orijinalin
   uzantısı var; `browse/…`, `full/…`, `orig/…` anahtarlarını sunucu kuruyor
   (apps/api/src/routes/admin.ts). Bu dosya sunucunun döndürdüğü anahtarı
   olduğu gibi kullanır — kendi anahtar kurmaya kalkarsa o savunma çöker.

   **Yarıda kalan yığın baştan başlamaz** (plan §15). Tamamlanan her katman
   `localStorage`'a yazılıyor; sekme kapanıp açıldığında aynı katman ikinci
   kez yüklenmiyor. Defterin anahtarı türevin sağlama toplamı: aynı kareyi
   yeniden üreten bir çalıştırma aynı toplamı bulur, yani defter dosya
   adından değil içerikten kimlik alıyor. */

import { key } from '@frawgraphy/shared';
import type { DerivedPhoto } from './pipeline.ts';

/** Sunucunun imzaladığı katmanlar. `orig` özel kovaya gider. */
export type UploadTier = 'browse' | 'full' | 'orig';

export type SignedUpload = {
  tier: UploadTier;
  key: string;
  bucket: string;
  url: string;
};

export type SignResponse = {
  uploads: SignedUpload[];
  expiresAt: string;
};

/** Tek bir katmanın PUT işini yapan şey. Testte sahte işlevle değiştirilir. */
export type PutFn = (
  url: string,
  body: Blob,
  contentType: string,
  onProgress?: (loaded: number, total: number) => void
) => Promise<void>;

export type UploadLedger = {
  /** Bu katman daha önce tamamlandı mı. */
  has: (checksum: string, tier: UploadTier) => boolean;
  /** Tamamlandı olarak işaretle. */
  mark: (checksum: string, tier: UploadTier) => void;
  /** Kaydı sil — yeniden yüklemek gerektiğinde. */
  forget: (checksum: string) => void;
};

export type UploadOptions = {
  /** Kare kimliği — sunucu anahtarları bundan kuruyor. */
  photoId: string;
  /** Orijinalin uzantısı; kapalı bir kümeden geliyor (sunucu doğruluyor). */
  originalExt: string;
  derived: DerivedPhoto;
  /** Orijinal dosyanın kendisi. Verilmezse `orig` katmanı atlanır. */
  original?: Blob | undefined;
  /** İmza ucunun adresi. Ayrı köken gerekmiyor — Worker aynı kökende. */
  signUrl?: string;
  /** Geçici paylaşılan giz; Access gelince kaldırılır (plan §12 notu). */
  adminSecret?: string | undefined;
  fetchImpl?: typeof fetch;
  /** Ayrı kökenden çağıran panel `include` geçer (bkz. `signUploads`). */
  credentials?: RequestCredentials;
  put?: PutFn;
  ledger?: UploadLedger;
  onTier?: (tier: UploadTier, state: 'atlandı' | 'yüklendi' | 'hata') => void;
  onProgress?: (tier: UploadTier, loaded: number, total: number) => void;
};

export type UploadResult = {
  /** Yüklenen ya da zaten yüklü olan katmanların anahtarları. */
  keys: Partial<Record<UploadTier, string>>;
  /** Bu çalıştırmada gerçekten yüklenen katmanlar. */
  uploaded: UploadTier[];
  /** Defterde bulunup atlanan katmanlar. */
  skipped: UploadTier[];
};

export const LEDGER_KEY = 'fg_upload_ledger';

/** Katmanın içerik türü. R2 nesneyi bu başlıkla saklıyor. */
export function contentTypeFor(tier: UploadTier, originalExt: string): string {
  if (tier === 'browse') return 'image/webp';
  if (tier === 'full') return 'image/jpeg';
  /* Orijinal ham olabilir; bilinen uzantılar dışında genel tür verilir.
     Yanlış bir tür, dosyanın kendisini bozmaz ama indirme adını ve
     tarayıcı davranışını etkiler. */
  const ext = originalExt.toLowerCase();
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'tif' || ext === 'tiff') return 'image/tiff';
  return 'application/octet-stream';
}

/**
 * `localStorage` üzerine oturan defter.
 *
 * Her erişim try/catch içinde: özel pencerede, depolama kapalıyken ya da
 * kota dolduğunda `localStorage` **fırlatır**. Yükleme, defter yüzünden
 * durmamalı — defter bir hızlandırıcı, doğruluk koşulu değil. Okuma
 * başarısız olursa yığın baştan yüklenir; yavaş ama doğru.
 */
export function localLedger(storage?: Storage): UploadLedger {
  const store = storage ?? (typeof localStorage === 'undefined' ? undefined : localStorage);

  const read = (): Record<string, string[]> => {
    if (!store) return {};
    try {
      const raw = store.getItem(LEDGER_KEY);
      return raw ? (JSON.parse(raw) as Record<string, string[]>) : {};
    } catch {
      return {};
    }
  };

  const write = (data: Record<string, string[]>): void => {
    if (!store) return;
    try {
      store.setItem(LEDGER_KEY, JSON.stringify(data));
    } catch {
      /* Kota dolmuş olabilir. Sessiz geçmek doğru: alternatif, yüklemeyi
         defter yazılamadığı için durdurmak olurdu. */
    }
  };

  return {
    has(checksum, tier) {
      return (read()[checksum] ?? []).includes(tier);
    },
    mark(checksum, tier) {
      const data = read();
      const tiers = data[checksum] ?? [];
      if (!tiers.includes(tier)) tiers.push(tier);
      data[checksum] = tiers;
      write(data);
    },
    forget(checksum) {
      const data = read();
      delete data[checksum];
      write(data);
    }
  };
}

/** Hiçbir şey hatırlamayan defter — testte ve depolama yokken. */
export function nullLedger(): UploadLedger {
  return { has: () => false, mark: () => {}, forget: () => {} };
}

/**
 * `fetch` ile PUT.
 *
 * İlerleme bildirimi yok: `fetch` yükleme ilerlemesini vermiyor. Bunu
 * isteyen arayüz `xhrPut`'u geçirir. Varsayılanın `fetch` olması, bu
 * paketin Node'da da (testte) çalışmasını sağlıyor.
 */
export const fetchPut =
  (fetchImpl: typeof fetch = fetch): PutFn =>
  async (url, body, contentType) => {
    const res = await fetchImpl(url, {
      method: 'PUT',
      body,
      headers: { 'content-type': contentType }
    });
    if (!res.ok) {
      /* R2'nin hata gövdesi XML; ilk satırı teşhis için yeterli ve
         günlüğü şişirmiyor. */
      const detail = await res.text().catch(() => '');
      throw new Error(`PUT ${res.status}: ${detail.slice(0, 200)}`);
    }
  };

/**
 * `XMLHttpRequest` ile PUT — ilerleme çubuğu isteyen arayüz için.
 *
 * 2 GB'lık bir yığında yöneticiye "ne kadar kaldı" söylemek gerekiyor ve
 * `fetch` bunu veremiyor. Bu yüzden tarayıcıda XHR tercih edilir.
 */
export const xhrPut: PutFn = (url, body, contentType, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`PUT ${xhr.status}: ${String(xhr.responseText).slice(0, 200)}`));
    };
    xhr.onerror = () => reject(new Error('PUT ağ hatası'));
    xhr.onabort = () => reject(new Error('PUT iptal edildi'));
    xhr.send(body);
  });

/** İmza ucundan adresleri ister. */
export async function signUploads(
  photoId: string,
  originalExt: string,
  tiers: UploadTier[],
  options: {
    signUrl?: string;
    adminSecret?: string | undefined;
    fetchImpl?: typeof fetch;
    credentials?: RequestCredentials;
  } = {}
): Promise<SignResponse> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  /* Access geldiğinde bu başlık düşer; oturum çerezi kendiliğinden gider. */
  if (options.adminSecret) headers['X-Admin-Secret'] = options.adminSecret;

  const res = await fetchImpl(options.signUrl ?? '/api/admin/uploads/sign', {
    method: 'POST',
    headers,
    /* Çerez taşınmalı: oturum katmanı çerezle çalışıyor.

       Varsayılan `same-origin`, çünkü Worker sitenin kendi kökeninde duruyor
       (apps/api/src/index.ts). Yönetim paneli ise ayrı bir alt alan adında
       (`admin.frawgraphy.com`) ve oradan bakınca bu çağrı **ayrı köken**;
       `same-origin` çerezi hiç göndermez ve imza ucu 404 döner. Panel bu
       yüzden `include` geçiyor. Çerezin kendisi engel değil: `SameSite=Strict`
       aynı *siteyi* kısıtlıyor, alt alan adları aynı siteden sayılıyor. */
    credentials: options.credentials ?? 'same-origin',
    body: JSON.stringify({ photoId, originalExt, tiers })
  });

  if (!res.ok) {
    /* 404 burada "kilit reddetti" demek, "uç yok" demek değil — kilit
       bilerek 404 döndürüyor (apps/api/src/lib/admin.ts). Mesaj o yüzden
       ikisini birden söylüyor; yoksa yönetici olmayan bir uç arar. */
    if (res.status === 404) {
      throw new Error(
        'İmza ucu reddetti (404). Yönetici kilidi geçilemedi: oturum açık mı, ' +
          'ya da paylaşılan giz doğru mu?'
      );
    }
    if (res.status === 503) {
      throw new Error('Görsel deposu yapılandırılmamış (503): R2 API jetonu eksik.');
    }
    const detail = await res.text().catch(() => '');
    throw new Error(`İmza alınamadı (${res.status}): ${detail.slice(0, 200)}`);
  }
  return (await res.json()) as SignResponse;
}

/**
 * Bir karenin bütün katmanlarını yükler.
 *
 * Sıra bilinçli: `browse` → `full` → `orig`. Küçük olan önce gidiyor, çünkü
 * yarıda kesilen bir yığında elde kalması en değerli olan gezinme
 * katmanıdır — onunla site çalışır, büyütme katmanı sonra tamamlanabilir.
 * `orig` en sonda: en büyük dosya ve herkese açık hiçbir sayfa ona
 * bağlı değil.
 */
export async function uploadPhoto(options: UploadOptions): Promise<UploadResult> {
  const {
    photoId,
    originalExt,
    derived,
    original,
    adminSecret,
    fetchImpl,
    onTier,
    onProgress
  } = options;

  const ledger = options.ledger ?? nullLedger();
  const put = options.put ?? fetchPut(fetchImpl ?? fetch);

  const bodies: Partial<Record<UploadTier, Blob>> = {
    browse: derived.browse.blob,
    full: derived.full.blob
  };
  if (original) bodies.orig = original;

  const order: UploadTier[] = ['browse', 'full', 'orig'];
  const wanted = order.filter((tier) => bodies[tier] !== undefined);

  /* Defterde duranlar hiç imzalanmıyor: imza istemek de bir istek ve
     yeniden başlatılan bir 200 karelik yığında 200 gereksiz çağrı eder. */
  const skipped = wanted.filter((tier) => ledger.has(derived.checksum, tier));
  const todo = wanted.filter((tier) => !ledger.has(derived.checksum, tier));
  for (const tier of skipped) onTier?.(tier, 'atlandı');

  const keys: Partial<Record<UploadTier, string>> = {};
  const uploaded: UploadTier[] = [];

  if (todo.length === 0) return { keys, uploaded, skipped };

  const signed = await signUploads(photoId, originalExt, todo, {
    ...(options.signUrl !== undefined ? { signUrl: options.signUrl } : {}),
    adminSecret,
    ...(fetchImpl ? { fetchImpl } : {}),
    ...(options.credentials ? { credentials: options.credentials } : {})
  });

  /* Sunucu istenen katmanların hepsini döndürmek zorunda; eksik dönerse
     sessizce yarım yüklemek yerine durulur. */
  const byTier = new Map(signed.uploads.map((u) => [u.tier, u]));
  for (const tier of todo) {
    if (!byTier.has(tier)) throw new Error(`Sunucu ${tier} katmanı için adres döndürmedi.`);
  }

  for (const tier of todo) {
    const target = byTier.get(tier) as SignedUpload;
    const body = bodies[tier] as Blob;
    try {
      await put(target.url, body, contentTypeFor(tier, originalExt), (loaded, total) =>
        onProgress?.(tier, loaded, total)
      );
    } catch (err) {
      onTier?.(tier, 'hata');
      throw err;
    }
    /* Defter ancak PUT döndükten sonra yazılıyor. Önce yazmak, yarıda kalan
       bir yüklemeyi "tamamlandı" sayıp kalıcı bir boşluk bırakırdı. */
    ledger.mark(derived.checksum, tier);
    keys[tier] = target.key;
    uploaded.push(tier);
    onTier?.(tier, 'yüklendi');
  }

  for (const tier of skipped) {
    /* Atlananların anahtarı da sonuçta bulunmalı: çağıran taraf bunu
       `POST /api/admin/photos`'a gönderecek ve orada eksik anahtar,
       yüklenmiş bir nesneyi kayıp göstermek demek.

       Anahtar **paylaşılan yardımcıyla** kuruluyor, elle biçimlendirilmiyor.
       Sunucu da aynı `key.*` işlevlerini kullanıyor (routes/admin.ts), yani
       biçim tek bir yerde tanımlı. Burada `browse/${'${id}'}.webp` gibi bir dize
       elle yazılsaydı, uzantı ya da önek bir gün sunucuda değiştiğinde bu
       dal sessizce yanlış anahtar üretmeye başlardı — ve yanlış anahtar,
       yüklenmiş bir nesneyi kayıp göstermek demek. */
    keys[tier] = keyFor(tier, photoId, originalExt);
  }

  return { keys, uploaded, skipped };
}

/** Katmanın nesne anahtarı. Tek kaynak: `@frawgraphy/shared`'ın `key.*`'i. */
function keyFor(tier: UploadTier, photoId: string, originalExt: string): string {
  if (tier === 'browse') return key.browse(photoId);
  if (tier === 'full') return key.full(photoId);
  return key.orig(photoId, originalExt);
}
